import React, { lazy, Suspense, useEffect } from 'react';
import { ToastProvider } from './components/ToastProvider';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import VersionChecker from './components/VersionChecker';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Faculties = lazy(() => import('./pages/Faculties'));
const FacultyDetail = lazy(() => import('./pages/FacultyDetail'));
const DepartmentDetail = lazy(() => import('./pages/DepartmentDetail'));
const AdminBlockDetail = lazy(() => import('./pages/AdminBlockDetail'));
const Postgraduates = lazy(() => import('./pages/Postgraduates'));
const PostgraduateDetail = lazy(() => import('./pages/PostgraduateDetail'));
const Research = lazy(() => import('./pages/Research'));
const ResearchDetail = lazy(() => import('./pages/ResearchDetail'));
const Scedl = lazy(() => import('./pages/Scedl'));
const ScedlDetail = lazy(() => import('./pages/ScedlDetail'));
const Facilities = lazy(() => import('./pages/Facilities'));
const Administration = lazy(() => import('./pages/Administration'));
const FacilityDetail = lazy(() => import('./pages/FacilityDetail'));
const AmenityDetail = lazy(() => import('./pages/AmenityDetail'));
const BusinessCenterDetail = lazy(() => import('./pages/BusinessCenterDetail'));
const Chat = lazy(() => import('./pages/Chat'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const CampusMap = lazy(() => import('./pages/CampusMap'));
const Departments = lazy(() => import('./pages/Departments'));
const Notices = lazy(() => import('./pages/Notices'));
const Updates = lazy(() => import('./pages/Updates'));
const NoticeDetail = lazy(() => import('./pages/NoticeDetail'));
const UpdateDetail = lazy(() => import('./pages/UpdateDetail'));
const Documents = lazy(() => import('./pages/Documents'));
const DocumentDetail = lazy(() => import('./pages/DocumentDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminContactMessages = lazy(() => import('./pages/admin/AdminContactMessages'));
const AdminKnowledgeSources = lazy(() => import('./pages/admin/AdminKnowledgeSources'));
const ManageContent = lazy(() => import('./pages/admin/ManageContent'));
const ChatLogs = lazy(() => import('./pages/admin/ChatLogs'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const PrivacyPolicy = lazy(() => import('./pages/Legal').then(m => ({ default: m.PrivacyPolicy })));
const Disclaimer = lazy(() => import('./pages/Legal').then(m => ({ default: m.Disclaimer })));

const routePrefetchers = [
  { matches: (path) => path === '/', load: () => import('./pages/Home') },
  { matches: (path) => path === '/faculties', load: () => import('./pages/Faculties') },
  { matches: (path) => /^\/faculties\/[^/]+$/.test(path), load: () => import('./pages/FacultyDetail') },
  { matches: (path) => /^\/faculties\/[^/]+\/dept\/[^/]+$/.test(path), load: () => import('./pages/DepartmentDetail') },
  { matches: (path) => path === '/departments', load: () => import('./pages/Departments') },
  { matches: (path) => path === '/facilities', load: () => import('./pages/Facilities') },
  { matches: (path) => path === '/administration', load: () => import('./pages/Administration') },
  { matches: (path) => /^\/facilities\/[^/]+$/.test(path), load: () => import('./pages/FacilityDetail') },
  { matches: (path) => /^\/amenities\/[^/]+$/.test(path), load: () => import('./pages/AmenityDetail') },
  { matches: (path) => /^\/business-centers\/[^/]+$/.test(path), load: () => import('./pages/BusinessCenterDetail') },
  { matches: (path) => /^\/admin-blocks\/[^/]+$/.test(path), load: () => import('./pages/AdminBlockDetail') },
  { matches: (path) => path === '/postgraduate', load: () => import('./pages/Postgraduates') },
  { matches: (path) => /^\/postgraduate\/[^/]+$/.test(path), load: () => import('./pages/PostgraduateDetail') },
  { matches: (path) => path === '/research', load: () => import('./pages/Research') },
  { matches: (path) => /^\/research\/[^/]+$/.test(path), load: () => import('./pages/ResearchDetail') },
  { matches: (path) => path === '/scedl', load: () => import('./pages/Scedl') },
  { matches: (path) => /^\/scedl\/[^/]+$/.test(path), load: () => import('./pages/ScedlDetail') },
  { matches: (path) => path === '/map', load: () => import('./pages/CampusMap') },
  { matches: (path) => path === '/about', load: () => import('./pages/About') },
  { matches: (path) => path === '/contact', load: () => import('./pages/Contact') },
  { matches: (path) => path === '/notices', load: () => import('./pages/Notices') },
  { matches: (path) => /^\/notices\/[^/]+$/.test(path), load: () => import('./pages/NoticeDetail') },
  { matches: (path) => path === '/updates', load: () => import('./pages/Updates') },
  { matches: (path) => /^\/updates\/[^/]+$/.test(path), load: () => import('./pages/UpdateDetail') },
  { matches: (path) => path === '/documents', load: () => import('./pages/Documents') },
  { matches: (path) => /^\/documents\/[^/]+$/.test(path), load: () => import('./pages/DocumentDetail') },
  { matches: (path) => path === '/chat', load: () => import('./pages/Chat') },
  { matches: (path) => path === '/privacy', load: () => import('./pages/Legal') },
  { matches: (path) => path === '/disclaimer', load: () => import('./pages/Legal') },
  { matches: (path) => path === '/admin/login', load: () => import('./pages/admin/AdminLogin') },
  { matches: (path) => path === '/admin/dashboard', load: () => import('./pages/admin/Dashboard') },
  { matches: (path) => /^\/admin\/(faculties|departments|facilities|amenities|business-centers|admin-blocks|postgraduates|research|scedl|documents|notices|updates|staff)$/.test(path), load: () => import('./pages/admin/ManageContent') },
  { matches: (path) => path === '/admin/contact-messages', load: () => import('./pages/admin/AdminContactMessages') },
  { matches: (path) => path === '/admin/ai-knowledge-sources', load: () => import('./pages/admin/AdminKnowledgeSources') },
  { matches: (path) => path === '/admin/chat-logs', load: () => import('./pages/admin/ChatLogs') },
  { matches: (path) => path === '/admin/settings', load: () => import('./pages/admin/Settings') },
];

const prefetchRoute = (path) => {
  const route = routePrefetchers.find(({ matches }) => matches(path));
  route?.load();
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  useEffect(() => {
    const prefetchFromLink = (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || link.target === '_blank') return;

      const url = new URL(link.href, window.location.origin);
      if (url.origin === window.location.origin) prefetchRoute(url.pathname);
    };

    document.addEventListener('pointerover', prefetchFromLink, { passive: true });
    document.addEventListener('pointerdown', prefetchFromLink, { passive: true });
    document.addEventListener('focusin', prefetchFromLink, { passive: true });
    return () => {
      document.removeEventListener('pointerover', prefetchFromLink);
      document.removeEventListener('pointerdown', prefetchFromLink);
      document.removeEventListener('focusin', prefetchFromLink);
    };
  }, []);

  return (
    <ToastProvider>
      <VersionChecker />
      <Router>
        <ScrollToTop />
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
            <Routes>
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="faculties" element={<Faculties />} />
              <Route path="faculties/:id" element={<FacultyDetail />} />
              <Route path="faculties/:facultyId/dept/:departmentId" element={<DepartmentDetail />} />
              <Route path="departments" element={<Departments />} />
              <Route path="facilities" element={<Facilities />} />
              <Route path="administration" element={<Administration />} />
              <Route path="facilities/:id" element={<FacilityDetail />} />
              <Route path="amenities/:id" element={<AmenityDetail />} />
              <Route path="business-centers/:id" element={<BusinessCenterDetail />} />
              <Route path="admin-blocks/:id" element={<AdminBlockDetail />} />
              <Route path="postgraduate" element={<Postgraduates />} />
              <Route path="postgraduate/:id" element={<PostgraduateDetail />} />
              <Route path="research" element={<Research />} />
              <Route path="research/:id" element={<ResearchDetail />} />
              <Route path="scedl" element={<Scedl />} />
              <Route path="scedl/:id" element={<ScedlDetail />} />
              <Route path="map" element={<CampusMap />} />
              <Route path="about" element={<About />} />
              <Route path="contact" element={<Contact />} />
              <Route path="notices" element={<Notices />} />
              <Route path="notices/:id" element={<NoticeDetail />} />
              <Route path="updates" element={<Updates />} />
              <Route path="updates/:id" element={<UpdateDetail />} />
              <Route path="documents" element={<Documents />} />
              <Route path="documents/:id" element={<DocumentDetail />} />
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="disclaimer" element={<Disclaimer />} />
            </Route>
            <Route path="/chat" element={<Chat />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            {/* Protected Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="faculties" element={<ManageContent type="faculties" />} />
              <Route path="departments" element={<ManageContent type="departments" />} />
              <Route path="facilities" element={<ManageContent type="facilities" />} />
              <Route path="amenities" element={<ManageContent type="amenities" />} />
              <Route path="business-centers" element={<ManageContent type="business-centers" />} />
              <Route path="admin-blocks" element={<ManageContent type="admin-blocks" />} />
              <Route path="postgraduates" element={<ManageContent type="postgraduates" />} />
              <Route path="research" element={<ManageContent type="research" />} />
              <Route path="scedl" element={<ManageContent type="scedl" />} />
              <Route path="documents" element={<ManageContent type="documents" />} />
              <Route path="notices" element={<ManageContent type="notices" />} />
              <Route path="updates" element={<ManageContent type="updates" />} />
              <Route path="staff" element={<ManageContent type="staff" />} />
              <Route path="contact-messages" element={<AdminContactMessages />} />
              <Route path="ai-knowledge-sources" element={<AdminKnowledgeSources />} />
              <Route path="chat-logs" element={<ChatLogs />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </Suspense>
      </Router>
    </ToastProvider>
  );
}

export default App;
